import { formatDate, formatHours, formatMoney } from '../../lib/format';

/**
 * "Is this client's prepaid support bank a problem, and in what words?" — one
 * answer, shared by the clients LIST and the client PAGE.
 *
 * It exists because of the defect a critic found on `/support`: the projected
 * run-out date ignored block expiry, so a bank whose entire balance dies in
 * three weeks was painted green. The server now clamps the projection (see
 * `hour-bank.ts` — `projectedExhaustion` is the EARLIER of the pace draining
 * the bank and the hours expiring, with `runsOutReason` naming which) and this
 * module is how the clients domain stops throwing that away:
 *
 * - the list row carries the marker, so the client about to lose paid-for
 *   hours is visible in the scan, not one click inside a tab;
 * - the client page labels the date by its REASON — "they expire" is not
 *   "at this pace", and a 2.29 h/week burn does not empty 27.75 h in 23 days.
 *
 * Nothing here re-derives a figure. Balance, overage, the date, the days and
 * the reason all come from `HourBankService`; this file only chooses which of
 * them to say.
 */

/**
 * Inside six weeks. ONE horizon for "expiring soon", used by the list marker
 * and by the verdict, so the row and the page it opens cannot disagree about
 * whether an expiry is worth mentioning.
 */
export const EXPIRY_HORIZON_DAYS = 45;

/** The figures a bank signal needs — the shape BOTH the list row's `support`
 *  object and the full `SupportBankRow` can be read as. */
export interface BankFigures {
  status: 'good' | 'ok' | 'low' | 'exhausted' | 'overage' | null;
  balance: number;
  overage: number;
  runsOut: string | null;
  runsOutInDays: number | null;
  runsOutReason: 'burn' | 'expiry' | null;
}

export interface BankSignal {
  /** `bad` = hours already given away · `warn` = hours about to be lost. */
  tone: 'bad' | 'warn';
  /** Which clock is running — the list marker keys its glyph off this. */
  kind: 'overage' | 'empty' | 'expiry' | 'low';
  /** Short enough to sit under a client's name in a table cell. */
  text: string;
  /** The same fact with the date, the money and the consequence spelled out. */
  title: string;
}

/**
 * ⚠ A BALANCE OF PREPAID HOURS IS NEVER PRICED. THIS FILE USED TO PRICE IT.
 *
 * The marker read `27.75 h ≈ $4,163 expire in 23 days` and the tooltip repeated
 * the figure — `pricedHours(balance, rate)`, i.e. Σ(remaining hours × the
 * block's rate). That is precisely the deferred-revenue computation
 * (`support/purchase.service.ts` names the formula) that the owner ruled out:
 *
 *   *"hours are never refunded unless solid digital makes the call to do that…
 *   but that does not need to be logged anywhere or used in any of our
 *   decisions. It's just a FYI."* — Jesse, 2026-08-13
 *
 * It was not an FYI here: it sat in the scan column of `/clients` and drove a
 * primary action ("Review support — 27.75 h expire in 23 days"). The HOURS are
 * the operational fact and they stay, on every screen, in the unit `/support`
 * already uses. A rate prices a SALE — a top-up, an invoice for overage — never
 * a balance.
 */

/** "in 23 days" / "today" / "8 days ago" — matches `support/components`'s
 *  `relativeDays` wording so the two screens read the same. */
function inDays(days: number): string {
  if (days === 0) return 'today';
  if (days < 0) return `${Math.abs(days)} ${days === -1 ? 'day' : 'days'} ago`;
  return `in ${days} ${days === 1 ? 'day' : 'days'}`;
}

/**
 * The one marker a bank earns, or null when there is nothing to say.
 *
 * Order is by what has already happened: work given away → an empty bank →
 * hours that will expire unspent → a balance the burn rate is about to finish.
 * **Expiry outranks `status: 'low'`** on purpose — a client can be at 46% of
 * their bank and still lose every hour of it on a date the burn rate has no
 * say in, which is exactly the case the green pill used to hide.
 */
export function bankSignal(
  bank: BankFigures | null | undefined,
  rate?: number | null,
): BankSignal | null {
  if (!bank || bank.status === null) return null;

  if (bank.overage > 0) {
    // ⚠ THE OVERAGE MARKER COUNTS HOURS. IT DOES NOT PRICE THEM.
    //
    // It used to print `17.00 h over the bank · $2,805` — overage × the bank's
    // rate — three hundred pixels from an Unbilled column reading $2,985 for
    // the SAME 17 hours, and a critic read that as two prices for one set of
    // work. Two things were wrong with it:
    //
    //  1. `rate` here is `HourBankService`'s `defaultRate`: what a TOP-UP would
    //     be sold at (the last block's price, else the support agreement's rate).
    //     An invoice for delivered work is priced at the entries' own bill
    //     rates. On seed data the two coincide at $165; structurally they need
    //     not, and then the row would print a figure nothing will ever bill.
    //  2. Overage hours ARE the client's unbilled time, and the Unbilled column
    //     already prices them — from the rates the wizard will use, split into
    //     time + expenses so the addition is on screen (`unbilledSplit`).
    //
    // So the hours are stated once, priced once, in the column that owns money.
    // The top-up price survives in the tooltip, named as the top-up price.
    return {
      tone: 'bad',
      kind: 'overage',
      text: `${formatHours(bank.overage, { unit: true })} over the bank`,
      title:
        `${formatHours(bank.overage, { unit: true })} of support work has been delivered beyond ` +
        `every purchased block. Every hour logged from here is unpaid work. The Unbilled column ` +
        `prices those hours at the rates they will be invoiced at` +
        (rate
          ? `; ${formatMoney(rate)}/h is what another block would be SOLD at, which is a different question.`
          : '.'),
    };
  }

  if (bank.status === 'exhausted') {
    return {
      tone: 'bad',
      kind: 'empty',
      text: 'support bank empty',
      title: 'The prepaid support bank is empty — the next hour logged is work nobody has paid for.',
    };
  }

  const days = bank.runsOutInDays;
  if (
    bank.runsOutReason === 'expiry' &&
    bank.runsOut &&
    days !== null &&
    days <= EXPIRY_HORIZON_DAYS &&
    bank.balance > 0
  ) {
    return {
      tone: 'warn',
      kind: 'expiry',
      text: `${formatHours(bank.balance, { unit: true })} expire ${inDays(days)}`,
      title:
        `${formatHours(bank.balance, { unit: true })} of paid-for support hours expire on ` +
        `${formatDate(bank.runsOut)} — ${inDays(days)}. They are lost whether or not anyone ` +
        `burns them; top the bank up or extend the block.`,
    };
  }

  if (bank.status === 'low') {
    const when = days !== null && bank.runsOut ? ` · out ${inDays(days)}` : '';
    return {
      tone: 'warn',
      kind: 'low',
      text: `${formatHours(bank.balance, { unit: true })} left${when}`,
      title:
        `${formatHours(bank.balance, { unit: true })} left in the prepaid support bank` +
        (bank.runsOut ? `, gone by ${formatDate(bank.runsOut)} at the current pace.` : '.'),
    };
  }

  return null;
}
